import { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { startOfYear, endOfYear, format } from "date-fns";
import { getMetric } from "@/lib/reports/registry";
import { computeSummaryRow } from "@/lib/reports/summary";
import { useReportData } from "@/hooks/reports/use-report-data";
import type {
  DrillStep,
  Grain,
  ReportContext,
  ReportFilters,
  ReportPeriod,
} from "@/lib/reports/types";
import { Checkbox } from "@/components/ui/checkbox";
import { ReportMenu } from "./ReportMenu";
import { PeriodPicker } from "./PeriodPicker";
import { ReportBreadcrumb } from "./ReportBreadcrumb";
import { ReportTable } from "./ReportTable";
import ReportExportButton from "./ReportExportButton";
import CommissionReportExport from "./CommissionReportExport";
import PayrollBasisExport from "./PayrollBasisExport";
import ExtrasSalaryExport from "./ExtrasSalaryExport";
import ExtrasPaymentQueue from "./ExtrasPaymentQueue";
import { SrIcon } from "@/components/ui/SrIcon";

const ISO = "yyyy-MM-dd";
const MODES = ["maaned", "kvartal", "aar", "egendefinert"] as const;

/** Default-periode = inneværende år (matcher dashboardets YTD-rammeverk). */
function currentYearPeriod(): ReportPeriod {
  const now = new Date();
  return {
    mode: "aar",
    from: format(startOfYear(now), ISO),
    to: format(endOfYear(now), ISO),
  };
}

/** Leser periode fra URL-parametre, faller tilbake til inneværende år. */
function periodFromParams(sp: URLSearchParams): ReportPeriod {
  const mode = sp.get("mode");
  const from = sp.get("fra");
  const to = sp.get("til");
  if (mode && from && to && (MODES as readonly string[]).includes(mode)) {
    return { mode: mode as ReportPeriod["mode"], from, to };
  }
  return currentYearPeriod();
}

export function ReportShell({
  defaultMetricId = "faktureringsgrad",
}: {
  defaultMetricId?: string;
}) {
  const [searchParams, setSearchParams] = useSearchParams();
  // Initialiser fra URL så valgt rapport + periode overlever reload.
  const [metricId, setMetricId] = useState(() => {
    const r = searchParams.get("rapport");
    return r && getMetric(r) ? r : defaultMetricId;
  });
  const [period, setPeriod] = useState<ReportPeriod>(() => periodFromParams(searchParams));
  const [drillPath, setDrillPath] = useState<DrillStep[]>([]);
  const [includeActive, setIncludeActive] = useState(false);
  const [includeCurrentMonth, setIncludeCurrentMonth] = useState(false);

  // Speil valgt rapport + periode til URL (replace, så vi ikke spammer historikken).
  useEffect(() => {
    const next = new URLSearchParams(searchParams);
    next.set("rapport", metricId);
    next.set("mode", period.mode);
    next.set("fra", period.from);
    next.set("til", period.to);
    setSearchParams(next, { replace: true });
    // searchParams/setSearchParams er stabile fra react-router; vi synker kun på state-endring.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [metricId, period]);

  const metric = getMetric(metricId) ?? getMetric("faktureringsgrad")!;
  const grain: Grain = metric.drilldown[drillPath.length] ?? metric.defaultGrain;
  const filters: ReportFilters = {};
  if (metric.projectScopeToggle) filters.includeActive = includeActive;
  if (metric.currentMonthToggle) filters.includeCurrentMonth = includeCurrentMonth;
  const ctx: ReportContext = { period, filters, drillPath };

  // VIB-2720: `isError` MÅ leses her. Gjør den ikke det, faller `data` tilbake
  // til [], og ReportTable tegner en mislykket spørring som «Ingen data». Det
  // er nøyaktig slik PGRST201-feilen i faktureringsgrad-spørringen (VIB-994)
  // fikk ligge uoppdaget i tre måneder: rapporten så ut som en rapport uten
  // tall, ikke som en rapport som ikke klarte å hente dem.
  const { data = [], isLoading, isError, error } = useReportData(metricId, grain, ctx);
  const summary = computeSummaryRow(metric.columns, data);

  const handleSelectMetric = (id: string) => {
    setMetricId(id);
    setDrillPath([]);
    setIncludeActive(false);
    setIncludeCurrentMonth(false);
  };

  const handleDrill = (rowId: string) => {
    const row = data.find((r) => r.id === rowId);
    if (!row) return;
    if (drillPath.length + 1 < metric.drilldown.length) {
      setDrillPath([...drillPath, { grain, id: rowId, label: row.label }]);
    }
  };

  const breadcrumbPath: DrillStep[] = [
    { grain: metric.defaultGrain, id: "root", label: metric.label },
    ...drillPath,
  ];

  const handleJump = (index: number) => {
    if (index === 0) setDrillPath([]);
    else setDrillPath(drillPath.slice(0, index));
  };

  const handleBack = () => setDrillPath(drillPath.slice(0, -1));

  const exportFilename = `${metric.id}-${period.from}_${period.to}.csv`;

  return (
    <div className="flex gap-6 px-4 py-6">
      <aside className="w-[220px] shrink-0">
        <ReportMenu selectedId={metricId} onSelect={handleSelectMetric} />
      </aside>

      <main className="min-w-0 flex-1 space-y-5">
        <header className="space-y-1">
          <h1 className="font-display text-2xl tracking-wide">{metric.label}</h1>
          {metric.description && (
            <p className="max-w-2xl text-sm text-muted-foreground">
              {metric.description}
            </p>
          )}
        </header>

        <div className="flex flex-wrap items-end justify-between gap-4">
          <div className="flex flex-wrap items-end gap-4">
            <PeriodPicker value={period} onChange={setPeriod} />
            {metric.projectScopeToggle && (
              <label className="flex cursor-pointer items-center gap-2 pb-1 text-sm">
                <Checkbox
                  checked={includeActive}
                  onCheckedChange={(v) => setIncludeActive(v === true)}
                />
                Vis også aktive prosjekter
              </label>
            )}
            {metric.currentMonthToggle && (
              <label className="flex cursor-pointer items-center gap-2 pb-1 text-sm">
                <Checkbox
                  checked={includeCurrentMonth}
                  onCheckedChange={(v) => setIncludeCurrentMonth(v === true)}
                />
                Inkluder inneværende måned
              </label>
            )}
          </div>
          {/* Ingen eksportknapp ved feil: en CSV av et tomt resultat ville sett
              ut som et gyldig svar, og det er den forvekslingen vi fjerner. */}
          {isError ? null : metric.id === "provisjon" ? (
            // VIB-1284: provisjon eksporterer fullt beregningsgrunnlag
            // (rad per ansatt × prosjekt + sum-rader), ikke bare tabellkolonnene.
            <CommissionReportExport period={period} />
          ) : metric.id === "lonnsgrunnlag" ? (
            // VIB-1224: lønnsgrunnlaget eksporterer grunnlagsrader per føring.
            <PayrollBasisExport period={period} />
          ) : (
            <ReportExportButton
              columns={metric.columns}
              rows={data}
              filename={exportFilename}
            />
          )}
        </div>

        <ReportBreadcrumb
          path={breadcrumbPath}
          onJump={handleJump}
          canGoBack={drillPath.length > 0}
          onBack={handleBack}
        />

        <div className="rounded-lg border bg-card">
          {isLoading ? (
            <p className="py-8 text-center text-sm text-muted-foreground">
              Laster …
            </p>
          ) : isError ? (
            <div role="alert" className="flex gap-3 px-5 py-8">
              <SrIcon
                name="alert-triangle"
                className="mt-0.5 h-5 w-5 shrink-0 text-destructive"
              />
              <div className="space-y-2">
                <p className="text-sm font-medium">
                  Tallene kunne ikke hentes.
                </p>
                <p className="max-w-xl text-sm text-muted-foreground">
                  Rapporten er ikke tom, den feilet. Prøv å laste siden på nytt.
                  Står feilen seg, er det en feil i systemet og ikke i dataene
                  dine. Send meldingen under videre til utvikler.
                </p>
                {/* Den tekniske meldingen hører hjemme på skjermen, ikke bare i
                    konsollen: den som ser feilen er sjelden den som leter i
                    devtools, og en PostgREST-kode peker rett på årsaken. */}
                <code className="block max-w-xl break-words rounded bg-muted px-2 py-1 text-xs text-muted-foreground">
                  {error instanceof Error ? error.message : String(error)}
                </code>
              </div>
            </div>
          ) : (
            <ReportTable
              columns={metric.columns}
              rows={data}
              onDrill={handleDrill}
              summary={summary}
            />
          )}
        </div>

        {/* VIB-1283: statistlønn-CSV — uavhengig av valgt rapport/periode
            over (statister har ikke noe periodiserbart datofelt), egen-gatet
            på admin-rolle + feature-flag inne i komponenten. */}
        <ExtrasSalaryExport />

        {/* VIB-2092: DL-ens utbetalingsliste. Som CSV-en over er den uavhengig
            av valgt rapport og periode — en statist står her til hen er betalt,
            ikke til perioden er over. Egen-gatet på admin + flagg. */}
        <ExtrasPaymentQueue />
      </main>
    </div>
  );
}
